// src/ts/session-keepalive.ts
//
// Keeps a sign-in alive while the application is open, and says so plainly when it has ended.
//
// The case this is for: somebody opens the application, goes off to do something else for a
// few hours, and comes back to it. The sign-in cookie has a sliding fourteen-day window
// (Program.cs) that the server resets on any request, so the way to keep the session is
// simply to make a request now and then. That is all this does — /api/session/ping exists to
// be that request and does nothing else. See SessionController for the server half.
//
// Nothing here is needed for the cookie to survive a normal working day. It is for the tab
// left open over a holiday, and for the second job below.
//
// The second job is the more useful one. If the session does end — a fortnight untouched, a
// sign-out in another tab, a cookie cleared, the server restarted — the page says so, in
// place, straight away. Before this, the tab looked signed in until the next click, which
// then failed: a comment written and sent into nothing, an upload refused with no reason
// given. Being told beforehand is the difference between reloading and losing work.
//
// Deliberately not done: signing the user out on a timer, warning them that time is running
// short, or redirecting them to the login page when it runs out. Nothing here ends a session
// early, and nothing navigates away from a page that may have half-written work on it — the
// banner waits to be clicked.

// Every five minutes.
//
// The old site pinged every minute. This does not need to: the server only has to hear from
// the browser once in seven days for the window never to close, so the interval is set by
// what else it buys rather than by the cookie. Five minutes stays inside the twenty-minute
// idle timeout an IIS application pool has by default, which keeps the application warm
// enough that coming back to the tab does not mean waiting for a cold start, and it notices
// an ended session soon enough to be worth showing. It is also a twelfth of the traffic.
const PING_INTERVAL_MS = 5 * 60 * 1000;

let timer: number | undefined;
let lastPingAt = 0;

/**
 * Starts pinging, if anybody is signed in. Safe to call on every page; it does nothing on the
 * login page, or anywhere else with no signed-in user.
 */
export function initSessionKeepalive(): void {
  // The layouts report the signed-in user in a meta tag, which is empty when nobody is.
  // Same tag the fetch cache uses to tell whose cache it is holding (see main.ts).
  const user = document.querySelector<HTMLMetaElement>('meta[name="wf-user"]')?.content;
  if (!user) return;

  // Loading this page was itself a request, so the clock starts here: the cookie has just
  // been seen by the server, and the next ping is due five minutes from now, not five
  // minutes from whenever the tab was last looked at.
  lastPingAt = Date.now();
  timer = window.setInterval(ping, PING_INTERVAL_MS);

  // A hidden tab's timers are throttled by the browser, and a tab it decides to freeze
  // outright stops running altogether — which is the state the tab in question is most
  // likely to be in. So the interval is not trusted on its own: coming back to the tab
  // also pings, if it has been long enough, which both revives a stalled cycle and is the
  // first thing to happen when somebody returns to a session that may have ended.
  document.addEventListener("visibilitychange", () => {
    if (timer === undefined) return; // stopped: the session has already ended
    if (document.visibilityState !== "visible") return;
    if (Date.now() - lastPingAt >= PING_INTERVAL_MS) ping();
  });
}

async function ping(): Promise<void> {
  lastPingAt = Date.now();

  try {
    // no-store for both caches in the way. The browser's, and this application's own
    // localStorage cache of GET /api/ responses in main.ts — which would otherwise answer
    // this from storage for five minutes at a time, so the request keeping the session
    // alive would never reach the server at all.
    const res = await fetch("/api/session/ping", {
      credentials: "same-origin",
      cache: "no-store",
    });

    // Anything other than a clean answer is treated as "no news". A 500, a proxy's error
    // page, a captive portal — none of them mean the session has ended, and claiming it had
    // would be worse than waiting for the next ping. The endpoint answers 200 either way,
    // so a real ending always arrives as one.
    if (!res.ok) return;

    const body = await res.json() as { signedIn?: boolean };
    if (body.signedIn !== false) return;
  } catch {
    // Offline, or the server is down. Not signed out. Try again in five minutes.
    return;
  }

  stop();
  showSessionEnded();
  // For anything else on the page that talks to the server on its own and should stop now,
  // such as the live project activity in project-events.ts. A window event, because that
  // lives in the page's bundle and this in the layout's.
  window.dispatchEvent(new Event("wf:session-ended"));
}

function stop(): void {
  if (timer !== undefined) {
    window.clearInterval(timer);
    timer = undefined;
  }
}

/**
 * Says the session has ended, once, at the top of the page.
 *
 * Reloading is what it offers rather than a link to the login page, because where signing in
 * happens depends on how the application is configured — the local password page, or Entra —
 * and a reload asks the server, which knows. It also comes back to this page afterwards.
 */
function showSessionEnded(): void {
  if (document.getElementById("session-ended")) return;

  const bar = document.createElement("div");
  bar.id = "session-ended";
  bar.className = "session-ended";
  bar.setAttribute("role", "alert");
  bar.innerHTML =
    '<span class="material-symbols-outlined">lock_clock</span>' +
    "<span>Your session has ended. Anything you have not saved on this page will not be " +
    "sent — copy it somewhere safe before signing in again.</span>" +
    '<button type="button" class="button is-small">Sign in again</button>';

  bar.querySelector("button")?.addEventListener("click", () => location.reload());
  document.body.prepend(bar);
}
